import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getNationalitiesList } from '../../../../../api/countries/get-nationalities-list'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { maritalStatusEnumMapping } from '../../../../../lib/types/enums/staff-application/marital-status.enum'
import {
  PositionStatusEnum,
  positionStatusEnumMapping
} from '../../../../../lib/types/enums/staff-application/position-status.enum'
import { HostFamily } from '../../../../../lib/types/models/host-family/host-family'
import BirthDateWithAge from '../../../../elements/birth-date-with-age'
import LabelWithText from '../../../../elements/label-with-text'
import PhoneNumberDisplay from '../../../../elements/phone-number-display'

type ParentData = {
  firstName?: string
  lastName?: string
  nationality?: string
  phoneNumber?: string
  email?: string
  birthDate?: string
  profession?: string
  employmentStatus?: PositionStatusEnum
}

type Props = {
  data: ParentData
  view: 'complex' | 'simple'
}

const ParentPersonalInformation = ({ data, view }: Props) => {
  const { t } = useTranslate()

  const { data: nationalitiesList } = useQuery({
    queryKey: ['nationalities-list'],
    queryFn: getNationalitiesList
  })

  return (
    <div
      className={cn('grid w-full gap-4', {
        'grid-cols-5 mobile:grid-cols-1 tab:grid-cols-2 smallLaptop:grid-cols-4':
          view === 'complex',
        'grid-cols-4 mobile:grid-cols-1 tab:grid-cols-2 smallLaptop:grid-cols-2': view === 'simple'
      })}
    >
      <LabelWithText label="NOM" data={data.lastName?.toLocaleUpperCase()} />
      <LabelWithText
        label="Prénom"
        data={data.firstName ? StringFormatter.capitalize(data.firstName) : '-'}
      />
      {view === 'complex' && (
        <LabelWithText
          label="Nationalité"
          data={
            nationalitiesList && data.nationality
              ? nationalitiesList.find((nationality) => nationality.code === data.nationality)
                  ?.label
              : '-'
          }
        />
      )}
      <LabelWithText
        label="Téléphone"
        data={data.phoneNumber ? <PhoneNumberDisplay phoneNumber={data.phoneNumber} /> : '-'}
      />
      {view === 'complex' && (
        <>
          <LabelWithText label="Email" data={data.email} className="break-all" />
          <BirthDateWithAge birthDate={data.birthDate} />
          <div />
        </>
      )}
      <LabelWithText
        label="Profession"
        className={cn({
          'col-span-1': view === 'simple',
          'col-span-2': view === 'complex'
        })}
        data={
          <div className="flex items-center gap-1">
            <Badge text={valueRender(data.profession)} variant="grey" />
            {data.profession && (
              <Badge
                text={valueRender(data.employmentStatus, (status) =>
                  t(positionStatusEnumMapping[status])
                )}
                variant="blue"
              />
            )}
          </div>
        }
      />
    </div>
  )
}

type HostFamilyParentInformationProps = {
  hostFamily: HostFamily
  className?: string
  view: 'complex' | 'simple'
}

const HostFamilyParentInformation = ({
  view,
  hostFamily,
  className
}: HostFamilyParentInformationProps) => {
  const { t } = useTranslate()
  return (
    <Box className={cn('bg-white-hover rounded-lg border border-grey-light', className)}>
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        {view === 'simple'
          ? 'Informations sur la famille-hôtesse'
          : 'Informations sur les parents'}
      </Typography>
      <div className="h-4" />
      {view === 'complex' && (
        <>
          <div className="flex gap-3 items-center">
            <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
              Situation de famille
            </Typography>
            <Badge
              text={t(maritalStatusEnumMapping[hostFamily.maritalStatus])}
              textColor={tailwindColors.secondary.DEFAULT}
              variant="grey"
            />
          </div>
          <div className="h-4" />
        </>
      )}
      <Typography variant="tag" color={tailwindColors.primary.dark.DEFAULT}>
        Madame
      </Typography>
      <div className="h-2" />
      <ParentPersonalInformation
        view={view}
        data={{
          firstName: hostFamily.parent1FirstName,
          lastName: hostFamily.parent1LastName,
          phoneNumber: hostFamily.parent1PhoneNumber,
          email: hostFamily.parent1Email,
          birthDate: hostFamily.parent1DateOfBirth,
          employmentStatus: hostFamily.parent1PositionStatus,
          profession: hostFamily.parent1Position,
          nationality: hostFamily.parent1Nationality
        }}
      />
      <div className="h-4" />
      <Typography variant="tag" color={tailwindColors.primary.dark.DEFAULT}>
        Monsieur
      </Typography>
      <div className="h-2" />
      <ParentPersonalInformation
        view={view}
        data={{
          firstName: hostFamily.parent2FirstName,
          lastName: hostFamily.parent2LastName,
          phoneNumber: hostFamily.parent2PhoneNumber,
          email: hostFamily.parent2Email,
          birthDate: hostFamily.parent2DateOfBirth,
          employmentStatus: hostFamily.parent2PositionStatus,
          profession: hostFamily.parent2Position,
          nationality: hostFamily.parent2Nationality
        }}
      />
    </Box>
  )
}

export default HostFamilyParentInformation
